import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormGroup,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import { captureException } from "@sentry/react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { fillLabels, type LidarClearanceLabel } from "./labelsApi";
import type { LidarClearanceAxis } from "./schemas";
import t from "./translations";

type Props = {
  open: boolean;
  axes: LidarClearanceAxis[];
  // Every label already saved for this system - used only to surface which
  // axis combinations already have labels, so the fill dialog doesn't ask
  // the user to generate the same combination twice.
  labels: LidarClearanceLabel[];
  region: string;
  systemId: number;
  onClose: () => void;
  onFilled: (labels: LidarClearanceLabel[]) => void;
};

// This dialog only ever generates combinations (2+ axes) - a single axis
// already has its own quick fill action next to its own section, so the
// flow here is pick one axis, then pick the others to combine it with.
export default function FillCombinationsDialog({
  open,
  axes,
  labels,
  region,
  systemId,
  onClose,
  onFilled,
}: Props) {
  const [primaryAxisId, setPrimaryAxisId] = useState<number | null>(null);
  const [secondaryAxisIds, setSecondaryAxisIds] = useState<Set<number>>(
    new Set()
  );
  const [filling, setFilling] = useState(false);

  useEffect(() => {
    if (open) {
      // A primary axis is always selected once the dialog is open - default
      // to the first one rather than leaving the combine-with column empty.
      setPrimaryAxisId(axes[0]?.id ?? null);
      setSecondaryAxisIds(new Set());
    }
  }, [open]);

  const axisNameById = useMemo(
    () => new Map(axes.map((axis) => [axis.id, axis.name_en])),
    [axes]
  );

  // Distinct axis-id combinations (not value combinations) already covered
  // by a saved label and including the current primary axis, so the picker
  // can point out ones that already exist.
  const relevantCombos = useMemo(() => {
    if (primaryAxisId === null) return [];
    const byKey = new Map<string, number[]>();
    for (const label of labels) {
      if (label.axis_values.length < 2) continue;
      const ids = label.axis_values.map((av) => av.axis_id);
      if (!ids.includes(primaryAxisId)) continue;
      const sorted = ids.slice().sort((a, b) => a - b);
      byKey.set(sorted.join(","), sorted);
    }
    return [...byKey.values()].sort(
      (a, b) => a.length - b.length || a.join(",").localeCompare(b.join(","))
    );
  }, [labels, primaryAxisId]);

  const axisIds = primaryAxisId === null ? [] : [primaryAxisId, ...secondaryAxisIds];
  const rawCombinationCount = axisIds.reduce((count, axisId) => {
    const axis = axes.find((candidate) => candidate.id === axisId);
    return axis ? count * (axis.max_value - axis.min_value + 1) : count;
  }, 1);
  // Every axis at 0 is skipped server-side - it's already the implicit
  // default for any point no label mentions, so it never gets its own row.
  const combinationCount = Math.max(rawCombinationCount - 1, 0);
  const canFill = axisIds.length > 1 && !filling;

  const selectPrimaryAxis = (axisId: number) => {
    setPrimaryAxisId(axisId);
    // The new primary can't also be a secondary - drop it if it was checked.
    setSecondaryAxisIds((prev) => {
      if (!prev.has(axisId)) return prev;
      const next = new Set(prev);
      next.delete(axisId);
      return next;
    });
  };

  const toggleSecondaryAxis = (axisId: number, checked: boolean) => {
    setSecondaryAxisIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(axisId);
      else next.delete(axisId);
      return next;
    });
  };

  const handleFill = async () => {
    setFilling(true);
    try {
      const created = await fillLabels(region, systemId, axisIds);
      onFilled(created);
      onClose();
    } catch (error) {
      captureException(error, { event_id: "lidarClearance.fillLabels" });
      toast.error(t.FailedToFillLabels);
    } finally {
      setFilling(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => (filling ? undefined : onClose())}
      fullWidth
      maxWidth="lg"
    >
      <DialogTitle>{t.FillCombinationsDialogTitle}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {t.FillCombinationsOnlyHint}
        </Typography>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary">
              {t.AxisLabel}
            </Typography>
            <List dense disablePadding>
              {axes.map((axis) => (
                <ListItemButton
                  key={axis.id}
                  selected={primaryAxisId === axis.id}
                  onClick={() => selectPrimaryAxis(axis.id)}
                  disabled={filling}
                >
                  <ListItemText primary={axis.name_en} />
                </ListItemButton>
              ))}
            </List>
          </Box>
          <Divider orientation="vertical" flexItem />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {primaryAxisId === null ? (
              <>
                <Typography variant="caption" color="text.secondary">
                  {t.CombineWithLabel}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {t.SelectAxisFirstHint}
                </Typography>
              </>
            ) : (
              <>
                {relevantCombos.length > 0 && (
                  <Box sx={{ mb: 1.5 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block" }}
                    >
                      {t.ExistingCombinationsLabel}
                    </Typography>
                    <Stack direction="row" spacing={0.75} sx={{ flexWrap: "wrap" }}>
                      {relevantCombos.map((ids) => (
                        <Chip
                          key={ids.join(",")}
                          variant="outlined"
                          sx={{
                            height: "auto",
                            "& .MuiChip-label": {
                              display: "block",
                              whiteSpace: "normal",
                              px: 0.5,
                              py: 0.5,
                            },
                          }}
                          label={
                            <Stack
                              direction="row"
                              spacing={0.5}
                              sx={{ flexWrap: "wrap" }}
                            >
                              {ids.map((id) => (
                                <Chip
                                  key={id}
                                  label={axisNameById.get(id) ?? id}
                                  size="small"
                                />
                              ))}
                            </Stack>
                          }
                        />
                      ))}
                    </Stack>
                  </Box>
                )}
                <Typography variant="caption" color="text.secondary">
                  {t.CombineWithLabel}
                </Typography>
                <FormGroup>
                  {axes
                    .filter((axis) => axis.id !== primaryAxisId)
                    .map((axis) => (
                      <FormControlLabel
                        key={axis.id}
                        control={
                          <Checkbox
                            checked={secondaryAxisIds.has(axis.id)}
                            onChange={(event) =>
                              toggleSecondaryAxis(axis.id, event.target.checked)
                            }
                            disabled={filling}
                          />
                        }
                        label={axis.name_en}
                      />
                    ))}
                </FormGroup>
              </>
            )}
          </Box>
        </Box>
        {axisIds.length > 0 && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 1, display: "block" }}
          >
            {t.FillCombinationCountHint(combinationCount)}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={filling}>
          {t.CancelButton}
        </Button>
        <Button
          onClick={handleFill}
          variant="contained"
          disabled={!canFill}
          startIcon={
            filling ? <CircularProgress size={16} color="inherit" /> : undefined
          }
        >
          {t.FillButton}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
